import React from 'react';
import { CloudServerOutlined, DatabaseOutlined, DollarCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import { ExecutivePresentation, InventoryRow, SummaryMetric, TabSection } from './types';
import {
  buildSecurityRiskItems,
  buildSecurityRiskSubtitle,
  buildServices,
  dataSourceTone,
  formatDataSource,
  normalizeStatusTone,
  numberFormatter,
  resolveCustomerLabel,
  toArray
} from './shared';

const buildSummaryMetrics = (data: any): SummaryMetric[] => {
  const opportunities = toArray(data?.optimizationOpportunities?.opportunities);
  const financialOpportunities = opportunities.filter(item =>
    Number(item?.netMonthlySavings || 0) > 0 &&
    item?.financialStatus === 'identified' &&
    ['cost', 'rightsizing', 'storage', 'network', 'commitment'].includes(item?.category)
  );
  const localRightsizing = opportunities.filter(item =>
    item?.category === 'rightsizing' &&
    item?.source !== 'aws-compute-optimizer' &&
    item?.source !== 'aws-cost-explorer-rightsizing'
  );
  const localRightsizingSavings = localRightsizing.reduce(
    (sum, item) => sum + Number(item?.monthlyImpact || 0),
    0
  );
  const xknowStatus = data?.xknowOptimizationAnalysis?.status;

  return [
  {
    title: 'Cobertura da Análise AWS',
    count: data?.nativeOptimization?.status === 'complete'
      ? 'Completa'
      : data?.nativeOptimization?.status === 'partial'
        ? 'Parcial'
        : 'Indisponível',
    subtitle: data?.nativeOptimization?.status === 'partial'
      ? 'Parte das APIs nativas não retornou dados; consulte a aba Cobertura AWS.'
      : 'Situação das fontes oficiais usadas na análise.',
    accent: data?.nativeOptimization?.status === 'complete' ? '#16a34a' : '#f59e0b',
    icon: <SafetyOutlined />,
    drilldownKey: 'awsCoverage'
  },
  {
    title: 'Rightsizing Oficial AWS',
    count: data?.nativeOptimization?.rightsizing?.total || 0,
    value: data?.nativeOptimization?.rightsizing?.estimatedMonthlySavings || 0,
    subtitle: 'Recomendações nativas da AWS com custo atual, configuração sugerida e economia.',
    accent: '#ff9900',
    icon: <CloudServerOutlined />,
    drilldownKey: 'awsOfficialRightsizing'
  },
  {
    title: 'Savings Plans',
    count: data?.nativeOptimization?.savingsPlans?.recommendationCount || 0,
    value: data?.nativeOptimization?.savingsPlans?.estimatedMonthlySavings || 0,
    subtitle: `Economia bruta AWS antes de rightsizing • cobertura ${Number(data?.nativeOptimization?.savingsPlans?.coveragePercentage || 0).toFixed(1)}%.`,
    accent: '#8b5cf6',
    icon: <DollarCircleOutlined />,
    drilldownKey: 'awsSavingsPlans'
  },
  {
    title: 'Reserved Instances',
    count: data?.nativeOptimization?.reservations?.recommendationCount || 0,
    value: data?.nativeOptimization?.reservations?.estimatedMonthlySavings || 0,
    subtitle: `Cenário alternativo ao Savings Plans; não somado simultaneamente • cobertura ${Number(data?.nativeOptimization?.reservations?.coveragePercentage || 0).toFixed(1)}%.`,
    accent: '#0f766e',
    icon: <DollarCircleOutlined />,
    drilldownKey: 'awsReservations'
  },
  {
    title: 'XKnow AI Independente',
    count: data?.xknowOptimizationAnalysis?.summary?.opportunities || 0,
    value: data?.xknowOptimizationAnalysis?.summary?.probable_monthly_savings || 0,
    subtitle: xknowStatus === 'insufficient-data'
      ? 'Executada, mas sem evidência financeira suficiente para uma recomendação independente.'
      : 'Análise própria por métricas e custos, executada em paralelo às recomendações AWS.',
    accent: '#0891b2',
    icon: <DollarCircleOutlined />,
    drilldownKey: 'xknowOptimization'
  },
  {
    title: 'Otimização Priorizada',
    count: financialOpportunities.length,
    value: data?.optimizationOpportunities?.estimatedMonthlySavings || data?.executiveSummary?.netMonthlySavings || 0,
    subtitle: 'Economia líquida após remover sobreposição entre ações por recurso, Savings Plans e RI.',
    accent: '#16a34a',
    icon: <DollarCircleOutlined />,
    drilldownKey: 'annualSavings'
  },
  {
    title: 'Volumes e Recursos Ociosos',
    count: data?.executiveSummary?.idleResources?.total || 0,
    subtitle: 'EBS, IPs elasticos e ativos sem uso.',
    accent: '#f59e0b',
    icon: <DatabaseOutlined />,
    drilldownKey: 'idle'
  },
  {
    title: 'Alertas de Segurança',
    count: data?.executiveSummary?.securityAlerts?.total || 0,
    subtitle: 'Pontos de atencao para postura da conta.',
    accent: '#ef4444',
    icon: <SafetyOutlined />,
    drilldownKey: 'security'
  },
  {
    title: 'Rightsizing XKnow/CloudWatch',
    count: localRightsizing.length,
    value: localRightsizingSavings,
    subtitle: 'Sinais próprios de superdimensionamento, separados das recomendações oficiais AWS.',
    accent: '#2563eb',
    icon: <CloudServerOutlined />,
    drilldownKey: 'xknowRightsizing'
  }
  ];
};

const buildTabs = (data: any): TabSection[] => {
  const nativePeriod = data?.nativeOptimization?.period;
  const periodLabel = nativePeriod?.Start && nativePeriod?.End
    ? `${nativePeriod.Start} a ${nativePeriod.End}`
    : 'Período não informado pela AWS';
  const unavailableReason = (reason: string) => {
    if (reason === 'DataUnavailableException') {
      return 'A AWS não possui dados suficientes para esta métrica no período. Isso é comum quando não há compromisso ativo.';
    }
    if (/AccessDenied/i.test(reason || '')) {
      return 'A role conectada não autorizou esta consulta. Atualize a policy de onboarding.';
    }
    return reason || 'Fonte temporariamente indisponível.';
  };
  const nativeCoverage = [
    {
      name: 'Reconciliação financeira',
      type: 'Economia bruta → ajuste de sobreposição → economia líquida',
      badge: `${numberFormatter(Number(data?.optimizationOpportunities?.savingsReconciliation?.netMonthlySavings || 0), 'aws')}/mês líquido`,
      badgeTone: 'success' as const,
      description: [
        `Ações On-Demand: ${numberFormatter(Number(data?.optimizationOpportunities?.savingsReconciliation?.grossOnDemandMonthlySavings || 0), 'aws')}/mês.`,
        `Melhor compromisso bruto: ${numberFormatter(Number(data?.optimizationOpportunities?.savingsReconciliation?.grossCommitmentMonthlySavings || 0), 'aws')}/mês.`,
        `Sobreposição removida: ${numberFormatter(Number(data?.optimizationOpportunities?.savingsReconciliation?.overlapAdjustment || 0), 'aws')}/mês.`,
        `Compromisso líquido: ${numberFormatter(Number(data?.optimizationOpportunities?.savingsReconciliation?.netCommitmentMonthlySavings || 0), 'aws')}/mês.`
      ].join(' '),
      meta: `${Number(data?.optimizationOpportunities?.savingsReconciliation?.identifiedCount || 0)} oportunidades identificadas • ${Number(data?.optimizationOpportunities?.savingsReconciliation?.validationCount || 0)} a validar`,
      sourceLabel: 'Regra de reconciliação XKnow',
      sourceTone: 'success' as const
    },
    {
      name: 'Rightsizing oficial AWS',
      type: 'AWS Cost Explorer / Compute Optimizer',
      badge: `${Number(data?.nativeOptimization?.rightsizing?.total || 0)} recomendação(ões)`,
      badgeTone: Number(data?.nativeOptimization?.rightsizing?.total || 0) > 0 ? 'success' as const : 'info' as const,
      description: Number(data?.nativeOptimization?.rightsizing?.total || 0) > 0
        ? `Economia oficial estimada em ${numberFormatter(Number(data.nativeOptimization.rightsizing.estimatedMonthlySavings || 0), 'aws')}/mês.`
        : 'A AWS respondeu à consulta, mas não indicou instâncias para rightsizing.',
      meta: `Período: ${periodLabel}`,
      sourceLabel: 'AWS oficial',
      sourceTone: 'success' as const
    },
    {
      name: 'Savings Plans',
      type: 'Compromisso de computação',
      badge: `${Number(data?.nativeOptimization?.savingsPlans?.recommendationCount || 0)} recomendação(ões)`,
      badgeTone: Number(data?.nativeOptimization?.savingsPlans?.recommendationCount || 0) > 0 ? 'warning' as const : 'info' as const,
      description: `Cobertura ${Number(data?.nativeOptimization?.savingsPlans?.coveragePercentage || 0).toFixed(1)}% • utilização ${Number(data?.nativeOptimization?.savingsPlans?.utilizationPercentage || 0).toFixed(1)}% • economia ${numberFormatter(Number(data?.nativeOptimization?.savingsPlans?.estimatedMonthlySavings || 0), 'aws')}/mês.`,
      meta: `Período: ${periodLabel}`,
      sourceLabel: 'AWS Cost Explorer',
      sourceTone: 'success' as const
    },
    {
      name: 'Reserved Instances',
      type: 'Compromisso por serviço',
      badge: `${Number(data?.nativeOptimization?.reservations?.recommendationCount || 0)} recomendação(ões)`,
      badgeTone: Number(data?.nativeOptimization?.reservations?.recommendationCount || 0) > 0 ? 'warning' as const : 'success' as const,
      description: `Cobertura ${Number(data?.nativeOptimization?.reservations?.coveragePercentage || 0).toFixed(1)}% • utilização ${Number(data?.nativeOptimization?.reservations?.utilizationPercentage || 0).toFixed(1)}% • economia ${numberFormatter(Number(data?.nativeOptimization?.reservations?.estimatedMonthlySavings || 0), 'aws')}/mês.`,
      meta: `Período: ${periodLabel}`,
      sourceLabel: 'AWS Cost Explorer',
      sourceTone: 'success' as const
    },
    ...toArray(data?.nativeOptimization?.unavailable).map((item: any, index) => ({
      name: item.source || `Fonte indisponível ${index + 1}`,
      type: 'Limitação de cobertura',
      badge: 'Dados indisponíveis',
      badgeTone: 'warning' as const,
      description: unavailableReason(item.reason),
      meta: `Código AWS: ${item.reason || 'não informado'} • Período: ${periodLabel}`,
      sourceLabel: 'Diagnóstico de cobertura',
      sourceTone: 'warning' as const
    })),
    ...toArray(data?.nativeOptimization?.notApplicable).map((item: any, index) => ({
      name: item.source || `Métrica não aplicável ${index + 1}`,
      type: 'Cobertura informativa',
      badge: 'Não aplicável',
      badgeTone: 'info' as const,
      description: 'Não há Savings Plan ativo no período; portanto, a AWS não possui utilização para calcular.',
      meta: `Código AWS: ${item.reason || 'DataUnavailableException'} • Período: ${periodLabel}`,
      sourceLabel: 'Diagnóstico de cobertura',
      sourceTone: 'info' as const
    }))
  ];
  const inventoryNameById = new Map(
    toArray(data?.inventoryDetails).flatMap((resource: any) => {
      const resourceId = String(resource?.resourceId || '').toLowerCase();
      const canonicalId = resourceId.replace(/:/g, '/').split('/').filter(Boolean).pop() || '';
      return [
        [resourceId, resource?.name],
        [canonicalId, resource?.name]
      ].filter(([key, value]) => key && value) as [string, string][];
    })
  );
  const xknowOptimization = toArray(data?.xknowOptimizationAnalysis?.opportunities).map((item, index) => {
    const resourceId = String(item.resource_id || '');
    const canonicalId = resourceId.toLowerCase().replace(/:/g, '/').split('/').filter(Boolean).pop() || '';
    const resourceName = item.resource_name
      || inventoryNameById.get(resourceId.toLowerCase())
      || inventoryNameById.get(canonicalId)
      || canonicalId;
    return {
    name: resourceName
      ? `${String(item.category || 'Otimização').replace(/-/g, ' ')}: ${resourceName}`
      : item.title || `Oportunidade XKnow ${index + 1}`,
    type: item.category || 'Otimização',
    badge: `Confiança ${Math.round(Number(item.confidence || 0) * 100)}%`,
    badgeTone: normalizeStatusTone(item.confidence_label),
    description: [
      item.current_sku || item.recommended_sku
        ? `Configuração: ${item.current_sku || 'N/A'} → ${item.recommended_sku || 'A validar'}.`
        : undefined,
      toArray(item.evidence).join(' • '),
      item.overlaps_provider ? 'Também recomendada pela AWS; não somar novamente.' : 'Sem sobreposição oficial identificada.',
      item.next_action
    ].filter(Boolean).join(' '),
    meta: [
      resourceName && resourceName !== canonicalId ? resourceName : undefined,
      item.current_sku && item.recommended_sku ? `${item.current_sku} → ${item.recommended_sku}` : undefined,
      item.resource_id
    ]
      .filter(Boolean).join(' • ') || 'Análise por serviço',
    cost: `${numberFormatter(Number(item.probable_monthly_savings || 0), 'aws')}/mes`,
    sourceLabel: 'XKnow AI independente',
    sourceTone: 'info' as const
  }});
  const ec2 = toArray(data?.ec2Details).map((item, index) => ({
    name: item.name || item.instanceId || `EC2 ${index + 1}`,
    type: item.size || item.type || 'EC2 Instance',
    badge: item.status || 'Monitorar',
    badgeTone: normalizeStatusTone(item.status),
    description: item.recommendation || item.reason || 'Instância monitorada para otimização.',
    meta: [
      item.location,
      typeof item.avgCpu === 'number' ? `CPU media ${item.avgCpu.toFixed(1)}%` : undefined,
      typeof item.maxCpu === 'number' ? `CPU pico ${item.maxCpu.toFixed(1)}%` : undefined
    ].filter(Boolean).join(' • '),
    cost: item.monthlyCost,
    sourceLabel: formatDataSource(item.source || (typeof item.avgCpu === 'number' ? 'aws-cloudwatch' : 'aws-service-apis')),
    sourceTone: dataSourceTone(item.source || (typeof item.avgCpu === 'number' ? 'aws-cloudwatch' : 'aws-service-apis'))
  }));

  const idle = toArray(data?.idleResourcesDetails).map((item, index) => ({
    name: item.name || item.resource || item.id || `Idle Resource ${index + 1}`,
    type: item.type || 'Idle Resource',
    badge: 'Ocioso',
    badgeTone: 'danger' as const,
    description: [
      item.details || item.recommendation || 'Remover ou anexar o recurso.',
      item.calculation ? `Cálculo: ${item.calculation}` : undefined
    ].filter(Boolean).join(' '),
    cost: item.monthlyCost,
    sourceLabel: formatDataSource(item.source),
    sourceTone: dataSourceTone(item.source)
  }));

  const rds = toArray(data?.rdsDetails).map((item, index) => ({
    name: item.name || `RDS ${index + 1}`,
    type: item.type || item.className || 'RDS Instance',
    badge: item.status || 'Monitorar',
    badgeTone: normalizeStatusTone(item.status),
    description: item.recommendation || 'Instancia RDS monitorada.',
    meta: [
      item.className,
      item.storageType,
      item.multiAz ? 'Multi-AZ' : 'Single-AZ',
      item.publiclyAccessible ? 'Publico' : 'Privado',
      typeof item.avgCpu === 'number' ? `CPU media ${item.avgCpu.toFixed(1)}%` : undefined,
      item.location
    ].filter(Boolean).join(' • '),
    sourceLabel: formatDataSource(item.source || (typeof item.avgCpu === 'number' ? 'aws-cloudwatch' : 'aws-service-apis')),
    sourceTone: dataSourceTone(item.source || (typeof item.avgCpu === 'number' ? 'aws-cloudwatch' : 'aws-service-apis'))
  }));

  const s3 = toArray(data?.s3Details).map((item, index) => ({
    name: item.name || `Bucket ${index + 1}`,
    type: item.type || 'S3 Bucket',
    badge: item.status || 'Monitorar',
    badgeTone: normalizeStatusTone(item.status),
    description: item.recommendation || 'Bucket S3 monitorado.',
    meta: [
      item.encryptionEnabled ? 'Criptografia ativa' : 'Sem criptografia',
      item.publicAccessBlocked ? 'Public Access Block ativo' : 'Sem Public Access Block'
    ].join(' • '),
    sourceLabel: 'AWS APIs',
    sourceTone: 'success' as const
  }));

  const security = toArray(data?.securityDetails).map((item, index) => ({
    name: item.title || item.resource || `Security Alert ${index + 1}`,
    type: item.type || 'Security',
    badge: item.severity || 'Medium',
    badgeTone: normalizeStatusTone(item.severity),
    description: [
      item.description || item.reason || 'Revisar configuração de segurança.',
      Number(item.affected || 0) > 0 ? `${item.affected} recurso(s) afetado(s).` : undefined
    ].filter(Boolean).join(' '),
    meta: toArray(item.details)
      .slice(0, 3)
      .map((detail: any) => detail.resourceName || detail.name)
      .filter(Boolean)
      .join(' • '),
    affectedResources: toArray(item.details).map((detail: any) => ({
      name: detail.resourceName || detail.name || detail.id || 'Recurso afetado',
      type: detail.resourceType || detail.type,
      issue: detail.issue,
      recommendation: detail.recommendation,
      location: detail.location
    })),
    sourceLabel: 'AWS APIs',
    sourceTone: 'success' as const
  }));

  const prioritizedRisks = toArray(data?.riskPrioritization?.prioritized_findings).map((item, index) => ({
    name: item.title || `Risco priorizado ${index + 1}`,
    type: item.resource_type || 'Cloud Risk',
    badge: `${Math.round(item.risk_score || 0)}/100`,
    badgeTone: normalizeStatusTone(item.priority),
    description: [
      item.business_impact,
      item.why_now,
      item.recommendation ? `Ação: ${item.recommendation}` : undefined
    ].filter(Boolean).join(' '),
    meta: [
      item.priority ? `Prioridade ${item.priority}` : undefined,
      item.effort ? `Esforço ${item.effort}` : undefined,
      item.confidence ? `Confiança ${Math.round(item.confidence * 100)}%` : undefined
    ].filter(Boolean).join(' • '),
    sourceLabel: 'Cloud Risk Prioritization AI',
    sourceTone: 'success' as const
  }));
  const securityRiskAnalysis = buildSecurityRiskItems(data);

  const optimizationOpportunities = toArray(data?.optimizationOpportunities?.opportunities).map((item, index) => ({
    name: item.resourceName || item.resourceId || item.title || `Oportunidade ${index + 1}`,
    type: item.service || item.resourceType || 'Optimization',
    badge: item.financialStatus === 'identified'
      ? 'Economia identificada'
      : item.financialStatus === 'scenario-alternative'
        ? 'Cenário alternativo'
        : 'A validar',
    badgeTone: item.financialStatus === 'identified' ? 'success' as const : 'warning' as const,
    description: [
      item.reason || item.title || 'Oportunidade de otimização identificada.',
      item.financialStatusReason,
      Number(item?.pricing?.currentMonthlyCost ?? item?.currentMonthlyCost ?? 0) > 0
        ? `Custo atual ${numberFormatter(Number(item?.pricing?.currentMonthlyCost ?? item?.currentMonthlyCost), 'aws')}/mês • custo recomendado ${numberFormatter(Number(item?.pricing?.recommendedMonthlyCost ?? item?.recommendedMonthlyCost ?? 0), 'aws')}/mês.`
        : undefined,
      Number(item.grossMonthlySavings || 0) > 0
        ? `Bruta ${numberFormatter(Number(item.grossMonthlySavings), 'aws')}/mês • líquida ${numberFormatter(Number(item.netMonthlySavings || 0), 'aws')}/mês • anual ${numberFormatter(Number(item.annualSavings || 0), 'aws')}.`
        : undefined,
      item.nextAction ? `Proxima acao: ${item.nextAction}` : undefined
    ].filter(Boolean).join(' '),
    meta: [
      item.currentSku || item.recommendedSku
        ? `Atual ${item.currentSku || 'N/A'} → recomendado ${item.recommendedSku || 'N/A'}`
        : undefined,
      item.category,
      Number(item.savingsPercentage || 0) > 0 ? `Economia ${Number(item.savingsPercentage).toFixed(1)}%` : undefined,
      item.implementationEffort ? `Esforco ${item.implementationEffort}` : undefined,
      item.confidence ? `Confianca ${Math.round(item.confidence)}%` : undefined,
      item.lookbackPeriodInDays ? `Período ${item.lookbackPeriodInDays} dias` : undefined,
      item.sourceLabel || item.source
    ].filter(Boolean).join(' • '),
    cost: Number(item.netMonthlySavings || 0) > 0
      ? `${numberFormatter(Number(item.netMonthlySavings), 'aws')}/mes`
      : undefined,
    sourceLabel: formatDataSource(item.source),
    sourceTone: dataSourceTone(item.source)
  }));

  const rightsizing = toArray(data?.upsizingAnalysis?.recommendations).map((item, index) => ({
    name: item.resourceName || item.instanceId || `Recommendation ${index + 1}`,
    type: item.type || 'Capacity',
    badge: item.action || 'Rightsize',
    badgeTone: normalizeStatusTone(item.action),
    description: item.reason || 'Ajuste de capacidade recomendado.',
    meta: [item.currentSku, item.recommendedSku].filter(Boolean).join(' -> '),
    cost: item.additionalCost ? numberFormatter(item.additionalCost, 'aws') : undefined,
    sourceLabel: formatDataSource(item.source),
    sourceTone: dataSourceTone(item.source)
  }));

  const eks = toArray(data?.eksDetails).map((item, index) => ({
    name: item.name || `EKS Cluster ${index + 1}`,
    type: item.type || 'EKS Cluster',
    badge: item.endpointAccess || item.status || 'Monitorar',
    badgeTone: normalizeStatusTone(item.status),
    description: item.recommendation || 'Cluster EKS monitorado.',
    meta: [
      item.platformVersion,
      typeof item.nodegroups === 'number' ? `${item.nodegroups} nodegroups` : undefined,
      item.arn
    ].filter(Boolean).join(' • '),
    sourceLabel: 'EKS API',
    sourceTone: 'success' as const
  }));

  const eksNodegroups = toArray(data?.eksNodegroupDetails).map((item, index) => ({
    name: item.name || `Nodegroup ${index + 1}`,
    type: item.type || 'EKS Nodegroup',
    badge: item.status || 'Monitorar',
    badgeTone: normalizeStatusTone(item.status),
    description: item.recommendation || 'Nodegroup EKS monitorado.',
    meta: [
      item.clusterName,
      item.instanceTypes,
      typeof item.desiredSize === 'number' ? `desired ${item.desiredSize}` : undefined,
      typeof item.minSize === 'number' ? `min ${item.minSize}` : undefined,
      typeof item.maxSize === 'number' ? `max ${item.maxSize}` : undefined,
      item.remoteAccessEnabled ? 'Remote access ativo' : 'Sem remote access'
    ].filter(Boolean).join(' • '),
    sourceLabel: 'EKS API',
    sourceTone: 'success' as const
  }));

  return [
    {
      key: 'aws-native-coverage',
      label: 'Cobertura AWS',
      subtitle: 'Fontes oficiais, período analisado, compromissos e limitações declaradas pela AWS.',
      items: nativeCoverage
    },
    {
      key: 'xknowOptimization',
      label: 'XKnow AI',
      subtitle: 'Recomendações independentes produzidas em paralelo às ferramentas nativas da AWS.',
      items: xknowOptimization
    },
    {
      key: 'optimization-ai',
      label: 'Otimização IA',
      subtitle: data?.optimizationOpportunities?.estimatedMonthlySavings
        ? `Economia mensal estimada de ${numberFormatter(data.optimizationOpportunities.estimatedMonthlySavings, 'aws')} priorizada por impacto e evidência.`
        : 'Oportunidades consolidadas por impacto, risco, esforço e evidência.',
      items: optimizationOpportunities
    },
    {
      key: 'security-ai',
      label: 'IA de Segurança',
      subtitle: buildSecurityRiskSubtitle(data),
      items: securityRiskAnalysis
    },
    {
      key: 'risk-ai',
      label: 'Priorização IA',
      subtitle: data?.riskPrioritization?.executive_summary || 'Riscos ordenados por impacto, exposição e esforço de correção.',
      items: prioritizedRisks
    },
    {
      key: 'compute',
      label: 'Compute',
      subtitle: 'Instancias, classes e direitos de uso de computacao.',
      items: ec2
    },
    {
      key: 'storage',
      label: 'Idle Resources',
      subtitle: 'Volumes, IPs e outros recursos sem uso.',
      items: idle
    },
    {
      key: 'database',
      label: 'RDS',
      subtitle: 'Bancos gerenciados e sua postura operacional.',
      items: rds
    },
    {
      key: 'object-storage',
      label: 'S3',
      subtitle: 'Buckets e controles basicos de seguranca.',
      items: s3
    },
    {
      key: 'security',
      label: 'Segurança',
      subtitle: 'Alertas de postura e exposicao operacional.',
      items: security
    },
    {
      key: 'capacity',
      label: 'Rightsizing',
      subtitle: 'Ajustes recomendados para EC2, RDS e afins.',
      items: rightsizing
    },
    {
      key: 'kubernetes',
      label: 'EKS',
      subtitle: 'Clusters Kubernetes e postura operacional do ambiente EKS.',
      items: eks
    },
    {
      key: 'nodegroups',
      label: 'Nodegroups',
      subtitle: 'Capacidade e configuração operacional dos nodegroups EKS.',
      items: eksNodegroups
    }
  ].filter(section => section.items.length > 0);
};

const buildInventoryRows = (data: any): InventoryRow[] => {
  const inventory = data?.inventory || {};
  return [
    { name: 'EC2 Instances', count: inventory.ec2Instances || 0 },
    { name: 'EC2 Instances (Databricks)', count: inventory.databricksManagedInstances || 0 },
    { name: 'EBS Volumes', count: inventory.ebsVolumes || 0 },
    { name: 'S3 Buckets', count: inventory.s3Buckets || 0 },
    { name: 'Elastic IPs', count: inventory.elasticIPs || 0 },
    { name: 'RDS Instances', count: inventory.rdsInstances || 0 },
    { name: 'Lambda Functions', count: inventory.lambdaFunctions || 0 },
    { name: 'EKS Clusters', count: inventory.eksClusters || 0 },
    { name: 'EKS Nodegroups', count: inventory.eksNodegroups || 0 }
  ];
};

export const createAwsPresentation = (data: any): ExecutivePresentation => ({
  provider: 'aws',
  title: 'AWS FinOps',
  providerLabel: 'Amazon Web Services',
  customerLabel: resolveCustomerLabel(data, data?.scope?.name || data?.account?.id),
  customerDomain: data?.customerDomain || data?.metadata?.customerDomain,
  customerLogoUrl: data?.customerLogoUrl || data?.metadata?.customerLogoUrl,
  scopeLabel: data?.scope?.name || data?.account?.id || 'Conta conectada via fluxo AWS',
  overview: {
    monthlyAverageCost: data?.overview?.monthlyAverageCost || 0,
    annualSavingsEstimate: data?.overview?.annualSavingsEstimate || 0,
    usageCost: data?.overview?.usageCost,
    creditCost: data?.overview?.creditCost,
    monthlyAverageCalculation: data?.overview?.monthlyAverageCalculation
  },
  executiveSummary: {
    netMonthlySavings: data?.executiveSummary?.netMonthlySavings || 0,
    idleTotal: data?.executiveSummary?.idleResources?.total || 0,
    securityTotal: data?.executiveSummary?.securityAlerts?.total || 0
  },
  summaryMetrics: buildSummaryMetrics(data),
  services: buildServices(data),
  inventoryRows: buildInventoryRows(data),
  tabs: buildTabs(data),
  rawData: data
});
